import { useParams } from "react-router-dom"
import { useToggle } from "react-use"
import { createContext, useContext, useEffect, useMemo, useState } from "react"

import { CURRENCY } from "utils/constants"
import { PaymentMethod } from "modules/ClientAccount/utils"
import { BookingStatus, BookingStatusMap, OfferedService } from "utils/interfaces"
import { Spinner } from "components/Spinner"
import { Footer } from "components"
import { ContextHandlers, ContextValue } from "modules/Payment/common/interfaces"
import { Header } from "modules/Payment/Header"
import { Heading } from "modules/Payment/Heading"
import { PAGES } from "modules/Payment/common/constants"
import { fetchBookingDetails, getBookingDetails } from "modules/Payment/common/api"
import { Content } from "modules/Payment/Content"
import { isGetBookingDetailsResponse } from "modules/BookingCommon/common/utils"
import { OnlineOrOffline, GetBookingDetailsResponse } from "modules/BookingCommon/common/interfaces"
import { Pages, PAYMENT_DONE_PARAMETER } from "modules/BookingCommon/common/constants"
import { ServicesGroup } from "utils/helpers/ServicesGroup"
import { SentryWrapper } from "utils/ThirdParty/Sentry/SentryWrapper"

const Context = createContext<[ContextValue, ContextHandlers] | null>(null)

function PaymentProvider() {
    const { id } = useParams<{ id: string }>()
    const [bookingDetails, setBookingDetails] = useState<GetBookingDetailsResponse | undefined>()
    const [bookingStatus, setBookingStatus] = useState<BookingStatus>()
    const [bookingDetailsError, toggleBookingDetailsError] = useToggle(false)
    const [bookSessionIsLoading, toggleBookSessionIsLoading] = useToggle(false)
    const [errorMessage, setBookSessionError] = useState<string | undefined>()
    const [currentPage, setCurrentPage] = useState<Pages>(PAGES[0])
    const [currency, setCurrency] = useState<CURRENCY>(CURRENCY.RUB)
    const [payByCard, setPayByCard] = useState<PaymentMethod | null>(null)
    const [onlineOrOffline, setOnlineOrOffline] = useState<OnlineOrOffline | undefined>()
    const [selectedDiscount, changeDiscount] = useState<number | undefined>()
    const [selectedService, setSelectedService] = useState<OfferedService | undefined>()
    const [serviceGroup, setServiceGroup] = useState<ServicesGroup | undefined>()

    const serviceGroups = useMemo(
        () => (bookingDetails ? ServicesGroup.from(bookingDetails.psy) : undefined),
        [bookingDetails]
    )

    useEffect(() => {
        const fetchAndSetBookingDetails = async () => {
            if (!id) {
                return
            }

            try {
                const waitStatusUpdate =
                    new URLSearchParams(window.location.search).get(PAYMENT_DONE_PARAMETER) === "1"

                const [getBookingDetailResponse, bookingDetailsResponse] = await Promise.all([
                    getBookingDetails(id),
                    fetchBookingDetails(id),
                ])

                if (!isGetBookingDetailsResponse(getBookingDetailResponse)) {
                    SentryWrapper.getInstance().captureException(
                        new Error("Invalid booking details response in Payment"),
                        {
                            tags: { id: id },
                            extra: {
                                response: getBookingDetailResponse,
                            },
                        }
                    )
                    return
                }

                setBookingDetails(getBookingDetailResponse)

                setOnlineOrOffline(getBookingDetailResponse.is_online ? "online" : "offline")
                setBookingStatus(bookingDetailsResponse.status)

                if (
                    bookingDetailsResponse.status === BookingStatusMap.PAID ||
                    (waitStatusUpdate && BookingStatusMap.PAY_STARTED)
                ) {
                    setCurrentPage(PAGES[2])
                    window.history.replaceState({ bookingPage: PAGES[0] }, document.title)
                    window.history.pushState({ bookingPage: PAGES[0] }, "", null)
                }
            } catch (error) {
                toggleBookingDetailsError()
                SentryWrapper.getInstance().captureException(
                    new Error("fetchAndSetBookingDetails error in Payment"),
                    {
                        tags: { id: id },
                        extra: {
                            error,
                        },
                    }
                )
            }
        }

        id && void fetchAndSetBookingDetails()
    }, [id])

    useEffect(() => {
        if (!serviceGroups) return

        setServiceGroup(
            serviceGroups.find(({ id }) => bookingDetails?.service_type.group.id === id)
        )
    }, [serviceGroups])

    useEffect(() => {
        if (!serviceGroup) return

        setSelectedService(
            serviceGroup?.services.find(
                (service) => service.service_type_id === bookingDetails?.service_type.id
            )
        )
    }, [serviceGroup])

    function nextPage() {
        const pageIndex = PAGES.findIndex((i) => i === currentPage)
        const nextPage = PAGES[pageIndex + 1]
        setCurrentPage(nextPage)
    }

    if (!bookingDetails) {
        return (
            <div className="redesign-container">
                <Spinner />
            </div>
        )
    }

    return (
        <Context.Provider
            value={[
                {
                    bookingDetails,
                    bookingDetailsError,
                    currency,
                    payByCard,
                    onlineOrOffline,
                    selectedDiscount,
                    selectedService,
                    serviceGroup,
                    currentPage,
                    isOnline: onlineOrOffline === "online",
                    bookSessionIsLoading,
                    errorMessage,
                    bookingStatus,
                },
                {
                    setPayByCard,
                    setCurrency,
                    changeDiscount,
                    setBookingDetails,
                    toggleBookSessionIsLoading,
                    setBookSessionError,
                    nextPage,
                    setBookingStatus,
                },
            ]}
        >
            <Header />
            <Heading />
            <Content />
            <Footer />
        </Context.Provider>
    )
}

const usePaymentContext = (): [ContextValue, ContextHandlers] => {
    const currentContext = useContext(Context)

    if (!currentContext) {
        throw new Error("usePaymentContext has to be used within <PaymentProvider.Provider>")
    }

    return currentContext
}

export { usePaymentContext, PaymentProvider }
